<script lang="ts">
  import { tick } from 'svelte';
  import {
    ACHIEVEMENTS, ASCENSION_MILESTONES, SKILLS, SKILL_MILESTONE_LEVELS, achievementProgress,
    type AchievementCategory, type GameState
  } from '@rsvjelle/game-core';
  import { formatNumber, SKILL_ICONS } from '../lib/display.ts';
  import { CURRENT_APP_VERSION, LATEST_APK_URL } from '../lib/app-update.ts';
  import type { PlayerSettings } from '../lib/player-settings.ts';
  import type { AccountSummary } from '../lib/account-status.ts';
  import AccountPanel from './AccountPanel.svelte';

  interface Props {
    gameState: GameState;
    onSave: () => Promise<void>;
    onReplace: (state: GameState) => Promise<void>;
    onLink: (userId: string) => Promise<void>;
    snapshot: () => GameState;
    settings: PlayerSettings;
    onSettingsChange: (settings: PlayerSettings) => void;
    onRestartOnboarding: () => void;
    onAccountStatusChange: (status: AccountSummary) => void;
  }
  let { gameState, onSave, onReplace, onLink, snapshot, settings, onSettingsChange, onRestartOnboarding, onAccountStatusChange }: Props = $props();
  let saving = $state(false);
  let panel = $state<'achievements' | 'milestones' | 'statistics' | 'settings' | null>(null);
  let unlockedMilestones = $derived(new Set(gameState.prestige.milestones ?? []));
  let unlockedAchievements = $derived(new Set(gameState.prestige.achievements ?? []));
  let milestoneTotal = $derived(SKILLS.length * SKILL_MILESTONE_LEVELS.length + ASCENSION_MILESTONES.length);
  let achievementPoints = $derived(ACHIEVEMENTS.filter((achievement) => unlockedAchievements.has(achievement.id)).reduce((sum, achievement) => sum + achievement.points, 0));
  let visibleAchievements = $derived(ACHIEVEMENTS.filter((achievement) => !achievement.hidden || unlockedAchievements.has(achievement.id)));
  let totalLevel = $derived(Object.values(gameState.run.skills).reduce((sum, skill) => sum + skill.level, 0));
  const categoryNames: Record<AchievementCategory, string> = { journey: 'Journey', mastery: 'Mastery', economy: 'Economy', craft: 'Realmcraft', ascension: 'Ascension', secret: 'Secret' };

  async function saveNow() { saving = true; await onSave(); saving = false; }
  function updateSetting(key: keyof PlayerSettings, enabled: boolean): void { onSettingsChange({ ...settings, [key]: enabled }); }
  async function togglePanel(next: NonNullable<typeof panel>): Promise<void> {
    panel = panel === next ? null : next;
    if (!panel) return;
    await tick();
    document.getElementById(`journal-panel-${panel}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
</script>

<section class="view-stack" aria-labelledby="more-title">
  <div class="section-heading"><div><p class="eyebrow">REALM RECORDS</p><h2 id="more-title">Adventure journal</h2></div></div>
  <div class="stat-grid">
    <article><span>⏱️</span><small>Active time</small><strong>{formatNumber(gameState.stats.activeSeconds / 3600, 1)}h</strong></article>
    <article><span>🌙</span><small>Offline time</small><strong>{formatNumber(gameState.stats.offlineSeconds / 3600, 1)}h</strong></article>
    <article><span>🏆</span><small>Achievements</small><strong>{unlockedAchievements.size}/{ACHIEVEMENTS.length}</strong></article>
    <article><span>✦</span><small>Ascensions</small><strong>{gameState.prestige.ascensions}</strong></article>
  </div>
  <div class="journal-list">
    <button class:active={panel === 'achievements'} type="button" onclick={() => void togglePanel('achievements')}><span>🏆</span><div><strong>Achievements</strong><small>Challenges, secrets and {achievementPoints} achievement points</small></div><b>›</b></button>
    <button class:active={panel === 'milestones'} type="button" onclick={() => void togglePanel('milestones')}><span>📍</span><div><strong>Milestones</strong><small>Level records and Ascension unlocks</small></div><b>›</b></button>
    <button class:active={panel === 'statistics'} type="button" onclick={() => void togglePanel('statistics')}><span>📊</span><div><strong>Statistics</strong><small>Your complete realm history</small></div><b>›</b></button>
    <button class:active={panel === 'settings'} type="button" onclick={() => void togglePanel('settings')}><span>⚙️</span><div><strong>Settings</strong><small>Audio, haptics, onboarding and Android</small></div><b>›</b></button>
  </div>

  {#if panel === 'achievements'}
    <article class="journal-panel achievement-panel" id="journal-panel-achievements">
      <div class="panel-title"><div><p class="eyebrow">REAL ACHIEVEMENTS</p><h3>{achievementPoints} points earned</h3></div><span>{unlockedAchievements.size}/{ACHIEVEMENTS.length} discovered</span></div>
      <div class="achievement-grid">
        {#each visibleAchievements as achievement}
          {@const unlocked = unlockedAchievements.has(achievement.id)}
          {@const progress = achievementProgress(gameState, achievement)}
          <div class:unlocked class="achievement-card">
            <span>{achievement.icon}</span>
            <div><small>{categoryNames[achievement.category]} · {achievement.points} pts</small><strong>{achievement.name}</strong><p>{achievement.description}</p></div>
            {#if unlocked}<b>✓</b>{:else}<em>{formatNumber(progress)} / {formatNumber(achievement.target)}</em>{/if}
          </div>
        {/each}
      </div>
      <small class="secret-count">Hidden achievements reveal themselves only after you earn them.</small>
    </article>
  {:else if panel === 'milestones'}
    <article class="journal-panel achievement-panel" id="journal-panel-milestones">
      <div class="panel-title"><div><p class="eyebrow">SKILL MILESTONES</p><h3>{unlockedMilestones.size} echoes recorded</h3></div><span>{milestoneTotal - unlockedMilestones.size} remaining</span></div>
      <div class="achievement-skills">
        {#each SKILLS as skillId}
          <div class="achievement-skill-row"><span class="skill-icon">{SKILL_ICONS[skillId]}</span><strong>{skillId}</strong><div>{#each SKILL_MILESTONE_LEVELS as level}<span class:unlocked={unlockedMilestones.has(`skill:${skillId}:${level}`)} title={`${skillId} level ${level}`}>{level}</span>{/each}</div></div>
        {/each}
      </div>
      <div class="ascension-roadmap">
        <p class="eyebrow">ASCENSION UNLOCKS</p>
        {#each ASCENSION_MILESTONES as milestone}
          <div class:unlocked={unlockedMilestones.has(milestone.id)}><span>{milestone.icon}</span><strong>A{milestone.target} · {milestone.title}</strong><small>{milestone.description}</small></div>
        {/each}
      </div>
    </article>
  {:else if panel === 'statistics'}
    <article class="journal-panel detailed-stats" id="journal-panel-statistics">
      <div><span>Total level</span><strong>{formatNumber(totalLevel, 0)}</strong></div><div><span>Lifetime resources</span><strong>{formatNumber(gameState.stats.totalResourceUnitsEarned)}</strong></div><div><span>Lifetime gold</span><strong>{formatNumber(gameState.stats.totalGoldEarned)}</strong></div><div><span>Lifetime Echo Shards</span><strong>{formatNumber(gameState.prestige.lifetimeEchoShards, 0)}</strong></div>
    </article>
  {:else if panel === 'settings'}
    <article class="journal-panel settings-panel" id="journal-panel-settings">
      <label><span><strong>Sound effects</strong><small>Short cues for purchases, levels and Ascension</small></span><input type="checkbox" checked={settings.sound} onchange={(event) => updateSetting('sound', event.currentTarget.checked)} /></label>
      <label><span><strong>Vibration</strong><small>Touch feedback on supported mobile devices</small></span><input type="checkbox" checked={settings.haptics} onchange={(event) => updateSetting('haptics', event.currentTarget.checked)} /></label>
      <button type="button" onclick={onRestartOnboarding}><span>🧭</span><span><strong>Replay introduction</strong><small>Restart the short interactive onboarding</small></span></button>
      <a class="apk-download" href={LATEST_APK_URL} download><span>🤖</span><span><strong>Download Android APK</strong><small>Latest signed beta, served directly by rs.vjelle.be</small></span><b>↓</b></a>
      <small class="install-warning">Android may ask permission to install apps from your browser. Only install APKs published by this project.</small>
      <small class="app-version">RSVjelle v2 · version {CURRENT_APP_VERSION}</small>
    </article>
  {/if}

  <AccountPanel {gameState} {onReplace} {onLink} {snapshot} onStatusChange={onAccountStatusChange} />
  <button class="save-button" type="button" onclick={saveNow} disabled={saving}>{saving ? 'Saving…' : 'Save progress now'}</button>
  <p class="save-detail">Save revision {gameState.revision} · Local IndexedDB remains available with two internal recovery backups</p>
</section>
